import React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { compose } from 'recompose'
import { push } from 'connected-react-router'
import debounce from 'lodash/debounce'
import uniq from 'lodash/uniq'
import { BCol, BForm, BLabel, BLink, BRow, BTitle } from '@prague-digi/ui-components'

import messages from '../../i18n'
import { form, FormInterface, intl, IntlInterface, wizard, WizardInterface } from '../../hoc'
import { ReduxState } from '../../types'
import {
  clientActions,
  modalDialogActions,
  modalLoaderActions,
  appSelectors,
  productActions,
} from '../../redux'
import { Footer } from './components'
import { BCheckbox, BMoneyInput, BTextInput, Notification, Slider } from '../../components/'
import { steps } from '../../utils/helpers'
import { API_ERROR, INSURANCE_QUESTION, SHOW_NEEDED_DOCS } from '../../constants/modals'
import { NotificationSeverityEnum } from '../../redux/reducers/notification/model'
import { getOpenApiInstance, Response } from '../../api/api'
import { Insurance, URL } from '../../constants'
import styles from './styles.scss'
import tickDone from '../../assets/tickDone.svg'
import moneyHand from '../../assets/money-hand.svg'
import formValidate from './LoanConfigurator.validate'
import { LoanRange } from '../../redux/reducers/codetable/model'
import { openLinkNewTab } from '../../utils/url'
import alertEmail from '../../assets/alert-email.svg'

interface LoanData {
  insuranceRequested: Insurance
  loanAmount: number
  term: number
}

interface Props
  extends IntlInterface,
    FormInterface,
    WizardInterface,
    ConnectedProps<typeof connector> {
  storedData: LoanData
  values: LoanData
  isRescoring?: boolean
}

export class LoanConfigurator extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    props.initialize(props.storedData)
  }

  componentDidMount() {
    this.doInit()
  }

  onSubmit = () => {
    this.props.handleSubmit(this.scheduleUpdate)()
  }

  onInitSuccess = () => {
    this.updateLimits()
  }

  onInitError = (errorType: Response) => {
    this.props.showModalDialog(
      API_ERROR({
        errorInfo: { type: errorType },
        onRetry: this.doInit,
      })
    )
  }

  getAllowedTerms = (min: number, max: number) => {
    const terms: number[] = []
    this.props.loanRanges.forEach((lr: LoanRange) => {
      lr.terms.forEach((t: number) => {
        if (min <= t && t <= max) {
          terms.push(t)
        }
      })
    })

    terms.sort()
    return uniq(terms)
  }

  getAmountText = () => this.props.formatCzechCurrency(this.props.amountTo)

  doInit = () => {
    this.props.initLoanConfigurator({
      data: { isRescoring: this.props.isRescoring },
      success: this.onInitSuccess,
      error: this.onInitError,
    })
  }

  doUpdate = (callback?: Function) => {
    this.props.showModalLoader()
    this.props.updateLoanConfigurator({
      success: () => {
        this.updateLimits()
        this.props.forceHideModalLoader()
        if (callback) {
          callback()
        }
      },
      error: (errorType: Response) => {
        this.props.forceHideModalLoader()

        this.props.showModalDialog(
          API_ERROR({ errorInfo: { type: errorType }, onRetry: this.doUpdate })
        )
      },
    })
  }

  commitReScoring = () => {
    this.props.commitLoanConfiguratorRe({
      success: this.props.wizard.gotoNext,
      error: (errorType: Response) =>
        this.props.showModalDialog(
          API_ERROR({ errorInfo: { type: errorType }, onRetry: this.commitReScoring })
        ),
    })
  }

  updateLimits = () => {
    this.props.change('amountFrom', this.props.amountFrom)
    this.props.change('amountTo', this.props.amountTo)
    this.props.change('termFrom', this.props.termFrom)
    this.props.change('termTo', this.props.termTo)
  }

  checkInsurance = (callback: Function) => () => {
    if (
      this.props.storedData.insuranceRequested === Insurance.NotAvailable ||
      this.props.values.insuranceRequested
    ) {
      callback()
    } else {
      this.props.showModalDialog(
        INSURANCE_QUESTION(
          () => {
            this.props.change('insuranceRequested', true)
            this.doUpdate(callback)
          },
          callback,
          this.props.insuranceMonthlyPayment
        )
      )
    }
  }

  finishOnline = () => {
    this.props.finishOnline()
    this.props.wizard.gotoNext()
  }

  finishIb = () => {
    getOpenApiInstance()
      .trackFinishInIb({})
      .then(() => {
        openLinkNewTab(URL.INTERNET_BANK)
      })
  }

  finishBranch = () => {
    getOpenApiInstance()
      .trackFinishOnBranch({})
      .then(() => {
        this.props.showModalDialog(SHOW_NEEDED_DOCS)
      })
  }

  scheduleUpdate = debounce(() => {
    this.doUpdate()
  }, 600)

  composeTitle = () => {
    if (this.props.isRescoring) {
      return this.props.translate(messages.loanConfigurator.rescoringTitle)
    }

    return this.props.translate(messages.loanConfigurator.titleApproved)
  }

  composeSubtitle = () => {
    if (this.props.isRescoring) {
      return this.props.translate(messages.loanConfigurator.rescoringSubtitle)
    } else if (this.props.scoringResultCalculation) {
      const scoringResult = this.props.scoringResultCalculation.toUpperCase()

      return this.props.translate(
        // @ts-ignore
        messages.loanConfigurator.subTitleApproved[scoringResult] as any,
        {
          maxAmount: this.getAmountText(),
        }
      )
    }
    return undefined
  }

  render() {
    const {
      amountFrom,
      amountTo,
      canFinishInIB,
      canFinishOnline,
      clientEmail,
      insuranceMonthlyPayment,
      isOnline,
      isRescoring,
      scoringResultCalculation,
      termFrom,
      termTo,
      translate,
      translateHTML,
    } = this.props

    const { loanAmount, term } = this.props.storedData

    const amountEditable: boolean = amountTo > amountFrom
    const termEditable: boolean = termTo > termFrom
    const amountSteps: number[] = steps(amountFrom, amountTo, 1000)
    const termSteps: number[] = this.getAllowedTerms(termFrom, termTo)

    return (
      scoringResultCalculation && (
        <BForm component="div">
          {canFinishInIB && !isRescoring && (
            <Notification
              severity={NotificationSeverityEnum.Normal}
              type="success"
              description={translate(messages.loanConfigurator.ibInfo)}
            />
          )}
          {!isRescoring && (
            <Notification
              severity={NotificationSeverityEnum.Normal}
              type="warning"
              description={translateHTML(messages.loanConfigurator.emailSent, {
                email: clientEmail,
              })}
              ownIconImg={alertEmail}
            />
          )}
          <BRow>
            <BCol
              theme="d-flex col-sm-auto pl-0 pl-sm-3 pr-0 pt-0 pt-sm-2 justify-content-center"
              xs={12}>
              <img src={tickDone} alt="done" className={styles.doneIcon} />
            </BCol>
            <BCol theme="col col-sm" xs={12}>
              <BTitle type="h3" theme="pb-1">
                {this.composeTitle()}
              </BTitle>
              <BTitle type="h4">{this.composeSubtitle()}</BTitle>
            </BCol>
          </BRow>

          <BTitle type="h4">{translate(messages.loanConfigurator.loanTitle)}</BTitle>

          {amountEditable ? (
            <div className={styles.controlBlock}>
              <div className={styles.inputLine}>
                <BLabel theme={styles.blockLabel}>
                  {translate(messages.loanConfigurator.loanAmount)}
                </BLabel>
                <div className={styles.moneyInputBox}>
                  <BMoneyInput
                    defaultValue={this.props.storedData.loanAmount}
                    fullDecimals={3}
                    maxValue={amountTo}
                    minValue={amountFrom}
                    name="loanAmount"
                    noMessage
                    onBlur={this.onSubmit}
                    theme={styles.moneyInput}
                  />
                </div>
              </div>
              <Slider
                name="loanAmount"
                value={loanAmount}
                rangeFrom={amountFrom}
                rangeFromSuffix={translate(messages.common.CZK)}
                rangeTo={amountTo}
                rangeToSuffix={translate(messages.common.CZK)}
                steps={amountSteps}
                hideSteps
                onChange={this.onSubmit}
                testid="loanAmount"
              />
            </div>
          ) : (
            <div className={styles.dataRow}>
              <div>{translate(messages.loanConfigurator.loanAmount)}</div>
              <div>
                <b className={styles.highlight}>{this.getAmountText()}</b>
              </div>
            </div>
          )}

          {termEditable ? (
            <div className={styles.controlBlock}>
              <div className={styles.inputLine}>
                <BLabel theme={styles.blockLabel}>
                  {translate(messages.loanConfigurator.term)}
                </BLabel>
                <div className={styles.termText}>{this.props.formatLoanDuration(term)}</div>
                <BTextInput
                  name="term"
                  value={term}
                  onBlur={this.onSubmit}
                  theme={styles.inputCtl}
                  testid="term"
                  noMessage
                  minValue={termFrom}
                  maxValue={termTo}
                />
              </div>
              <div>
                <Slider
                  name="term"
                  rangeFrom={termFrom}
                  rangeFromSuffix={translate(messages.loanConfigurator.termSliderRangeFrom)}
                  rangeTo={termTo}
                  rangeToSuffix={translate(messages.loanConfigurator.termSliderRangeTo)}
                  steps={termSteps}
                  onChange={this.onSubmit}
                  testid="term"
                />
              </div>
            </div>
          ) : (
            <div className={styles.dataRow}>
              <div>{translate(messages.loanConfigurator.term)}</div>
              <div>{term}</div>
            </div>
          )}

          <div className={styles.dataRow}>
            <div>{translate(messages.loanConfigurator.instalment)}</div>
            <div>
              <b className={styles.highlight}>
                {this.props.formatCzechCurrency(this.props.instalmentAmount)}
              </b>
            </div>
          </div>

          <div className={styles.dataRow}>
            <div>{translate(messages.loanConfigurator.interestRate)}</div>
            <div>
              {this.props.intl.formatNumber(this.props.primIntRate)} %,&nbsp;
              {translate(messages.loanConfigurator.rpsn)}&nbsp;
              {this.props.intl.formatNumber(this.props.rpsn)} %
            </div>
          </div>

          <div className={styles.dataRow}>
            <div>{translate(messages.loanConfigurator.feeAmount)}</div>
            <div>{this.props.formatCzechCurrency(this.props.feeAmount)}</div>
          </div>

          <div className={styles.dataRow}>
            <div>{translate(messages.loanConfigurator.totalAmount)}</div>
            <div>{this.props.formatCzechCurrency(this.props.totalAmount)}</div>
          </div>
          <br />
          {this.props.storedData.insuranceRequested !== Insurance.NotAvailable && (
            <div className={styles.insuranceBox}>
              <BRow>
                <BCol theme="d-flex col-sm-auto pr-0 pl-0 pl-sm-3 justify-content-center" xs={12}>
                  <div className={styles.bCheckboxWrapper}>
                    <BCheckbox
                      name="insuranceRequested"
                      onChange={this.onSubmit}
                      testid="insuranceRequested"
                    />
                  </div>
                </BCol>
                <BCol theme="col col-sm" xs={12}>
                  {translate(messages.loanConfigurator.insurance, {
                    monthlyPayment: insuranceMonthlyPayment,
                  })}
                  <br />
                  <BLink
                    type="link"
                    href={URL.MONETA_INSURANCE_INFO}
                    target="_blank"
                    message={translate(messages.loanConfigurator.insuranceLink)}
                  />
                </BCol>
                <BCol theme="d-flex col-sm-auto pl-0 pr-0 pr-sm-3 justify-content-center" xs={12}>
                  <img src={moneyHand} alt="done" className={styles.doneIcon} />
                </BCol>
              </BRow>
            </div>
          )}
          <Footer
            canFinishInIB={canFinishInIB}
            canFinishOnline={canFinishOnline && isOnline}
            commitReScoring={this.commitReScoring}
            finishBranch={this.checkInsurance(this.finishBranch)}
            finishIb={this.finishIb}
            finishOnline={this.checkInsurance(this.finishOnline)}
            isRescoring={isRescoring}
          />
        </BForm>
      )
    )
  }
}

const connector = connect(
  (state: ReduxState) => ({
    amountFrom: state.product.configuration?.amountFrom,
    amountTo: state.product.configuration?.amountTo,
    canFinishInIB: state.product.canFinishInIB,
    canFinishOnline: state.product.canFinishOnline,
    clientEmail: state.client.email,
    feeAmount: state.product.configuration?.feeAmount,
    instalmentAmount: state.product.configuration?.instalmentAmount,
    insuranceMonthlyPayment: state.product.configuration?.insuranceMonthlyPayment,
    isOnline: appSelectors.selectFFlagOnline(state),
    loanRanges: state.codetable.loanRanges,
    primIntRate: state.product.configuration?.primIntRate,
    rpsn: state.product.configuration?.rpsn,
    scoringResultCalculation: state.product.scoringResultCalculation,
    storedData: {
      loanAmount: state.product.configuration?.loanAmount,
      term: state.product.configuration?.term,
      insuranceRequested: state.product.insuranceRequested,
    },
    termFrom: state.product.configuration?.termFrom,
    termTo: state.product.configuration?.termTo,
    totalAmount: state.product.configuration?.totalAmount,
  }),
  {
    commitLoanConfiguratorRe: productActions.commitLoanConfiguratorRe,
    finishOnline: clientActions.finishOnline,
    forceHideModalLoader: modalLoaderActions.forceHideModalLoader,
    hideModalLoader: modalLoaderActions.hideModalLoader,
    initLoanConfigurator: productActions.initLoanConfigurator,
    push,
    showModalDialog: modalDialogActions.showModalDialog,
    showModalLoader: modalLoaderActions.showModalLoader,
    updateLoanConfigurator: productActions.updateLoanConfigurator,
  }
)

const LoanConfiguratorConnected = compose(
  wizard,
  connector,
  form({
    name: 'LoanConfigurator',
    validate: formValidate,
  }),
  intl
)(LoanConfigurator)

export default LoanConfiguratorConnected

export const LoanConfiguratorRe = (props: any) => (
  <LoanConfiguratorConnected {...props} isRescoring />
)
